// PROMPT 3.5 · Community Gallery Admin (Object-Storage backed)
// Simple env-password gate. Login → localStorage token (24h).
// Drag-drop / file-pick upload → object storage. List + soft-delete.

import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const TOKEN_KEY = "loadout.admin.community.token";
const MAX_BYTES = 8 * 1024 * 1024;
const ALLOWED = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"]);

function useToken() {
        const [token, setToken] = useState(() => {
                try { return window.localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
        });
        const save = useCallback((t) => {
                setToken(t);
                try { window.localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
        }, []);
        const clear = useCallback(() => {
                setToken("");
                try { window.localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
        }, []);
        return [token, save, clear];
}

function LoginPanel({ onLogin }) {
        const [pw, setPw] = useState("");
        const [busy, setBusy] = useState(false);
        const [err, setErr] = useState("");

        const submit = async (e) => {
                e.preventDefault();
                setErr("");
                setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/community/login`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ password: pw }),
                        });
                        if (!r.ok) {
                                setErr(r.status === 401 ? "Wrong password." : `Login failed (${r.status}).`);
                                return;
                        }
                        const j = await r.json();
                        onLogin(j.token);
                } catch (_e) {
                        setErr("Network error. Try again.");
                } finally {
                        setBusy(false);
                }
        };

        return (
                <form
                        onSubmit={submit}
                        data-testid="admin-community-login-form"
                        className="max-w-sm w-full border border-white/15 bg-[#0F0F0F] p-8"
                >
                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#FF9F1C]">
                                Loadout · Ops Console
                        </p>
                        <h1 className="mt-2 font-display text-3xl uppercase leading-tight text-white">
                                Community Gallery
                        </h1>
                        <p className="mt-4 font-sans text-sm text-white/60">
                                Enter the admin upload password to manage the operator loadouts strip.
                        </p>
                        <label className="mt-6 block font-mono text-[11px] tracking-[0.16em] uppercase text-white/50">
                                Password
                        </label>
                        <input
                                type="password"
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                autoFocus
                                data-testid="admin-community-password-input"
                                className="mt-2 w-full bg-black border border-white/20 focus:border-[#FF9F1C] outline-none text-white px-3 py-2 font-mono text-sm"
                        />
                        {err && (
                                <p
                                        data-testid="admin-community-login-error"
                                        className="mt-3 font-mono text-[11px] tracking-[0.14em] uppercase text-red-400"
                                >
                                        {err}
                                </p>
                        )}
                        <button
                                type="submit"
                                disabled={busy || !pw}
                                data-testid="admin-community-login-submit"
                                className="mt-6 w-full bg-[#FF9F1C] text-black font-mono text-[12px] tracking-[0.18em] uppercase py-3 disabled:opacity-40"
                        >
                                {busy ? "Signing in…" : "Sign In"}
                        </button>
                </form>
        );
}

function UploadZone({ token, onUploaded }) {
        const inputRef = useRef(null);
        const [busy, setBusy] = useState(false);
        const [dragging, setDragging] = useState(false);
        const [err, setErr] = useState("");
        const [progress, setProgress] = useState({ done: 0, total: 0 });

        const uploadOne = async (file, altText) => {
                const fd = new FormData();
                fd.append("file", file);
                if (altText) fd.append("alt_text", altText);
                const r = await fetch(`${BACKEND}/api/admin/community/upload`, {
                        method: "POST",
                        headers: { "Authorization": `Bearer ${token}` },
                        body: fd,
                });
                if (r.status === 401) throw new Error("session_expired");
                if (!r.ok) {
                        let detail = `HTTP ${r.status}`;
                        try { detail = (await r.json()).detail || detail; } catch { /* ignore */ }
                        throw new Error(detail);
                }
                return r.json();
        };

        const handleFiles = async (files) => {
                setErr("");
                const list = Array.from(files || []).filter((f) => {
                        if (!ALLOWED.has(f.type)) { setErr(`Unsupported file type: ${f.type || f.name}`); return false; }
                        if (f.size > MAX_BYTES) { setErr(`${f.name} exceeds 8 MB.`); return false; }
                        return true;
                });
                if (!list.length) return;
                setBusy(true);
                setProgress({ done: 0, total: list.length });
                try {
                        for (let i = 0; i < list.length; i++) {
                                try {
                                        await uploadOne(list[i], "");
                                        setProgress({ done: i + 1, total: list.length });
                                } catch (e) {
                                        if (e.message === "session_expired") {
                                                setErr("Session expired. Sign in again.");
                                                onUploaded({ sessionExpired: true });
                                                return;
                                        }
                                        setErr(`${list[i].name}: ${e.message}`);
                                }
                        }
                        onUploaded({ count: list.length });
                } finally {
                        setBusy(false);
                        setProgress({ done: 0, total: 0 });
                        if (inputRef.current) inputRef.current.value = "";
                }
        };

        return (
                <div
                        data-testid="admin-community-upload-zone"
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => {
                                e.preventDefault();
                                setDragging(false);
                                handleFiles(e.dataTransfer.files);
                        }}
                        className={`border-2 border-dashed p-10 text-center transition-colors ${
                                dragging ? "border-[#FF9F1C] bg-[#FF9F1C]/5" : "border-white/20 bg-[#0F0F0F]"
                        }`}
                >
                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-white/50">
                                Drag &amp; drop images here
                        </p>
                        <p className="mt-2 font-sans text-sm text-white/40">
                                JPG / PNG / WEBP / GIF · max 8 MB per file
                        </p>
                        <button
                                type="button"
                                onClick={() => inputRef.current?.click()}
                                disabled={busy}
                                data-testid="admin-community-pick-files"
                                className="mt-6 inline-block border border-white/30 hover:border-[#FF9F1C] text-white font-mono text-[12px] tracking-[0.18em] uppercase px-5 py-2 disabled:opacity-40"
                        >
                                {busy
                                        ? `Uploading… ${progress.done}/${progress.total}`
                                        : "Pick Files"}
                        </button>
                        <input
                                ref={inputRef}
                                type="file"
                                multiple
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                onChange={(e) => handleFiles(e.target.files)}
                                className="hidden"
                                data-testid="admin-community-file-input"
                        />
                        {err && (
                                <p
                                        data-testid="admin-community-upload-error"
                                        className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-red-400"
                                >
                                        {err}
                                </p>
                        )}
                </div>
        );
}

function ImageTile({ image, token, onDeleted }) {
        const [busy, setBusy] = useState(false);
        const [failed, setFailed] = useState(false);

        const remove = async () => {
                if (!window.confirm("Delete this image from the gallery?")) return;
                setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/community/${image.id}`, {
                                method: "DELETE",
                                headers: { "Authorization": `Bearer ${token}` },
                        });
                        if (r.status === 401) { onDeleted({ sessionExpired: true }); return; }
                        if (r.ok) onDeleted({ id: image.id });
                        else setFailed(true);
                } catch (_e) {
                        setFailed(true);
                } finally {
                        setBusy(false);
                }
        };

        return (
                <div
                        data-testid="admin-community-tile"
                        className="border border-white/10 bg-[#0F0F0F] overflow-hidden"
                >
                        <div style={{ aspectRatio: "1/1" }} className="bg-black">
                                <img
                                        src={`${BACKEND}${image.url}`}
                                        alt={image.alt_text || image.original_filename || "community upload"}
                                        loading="lazy"
                                        className="w-full h-full object-cover"
                                />
                        </div>
                        <div className="p-3">
                                <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-white/50 truncate">
                                        {image.original_filename || image.id.slice(0, 8)}
                                </p>
                                <p className="mt-1 font-mono text-[10px] text-white/30">
                                        {Math.round((image.size_bytes || 0) / 1024)} KB
                                </p>
                                <button
                                        type="button"
                                        onClick={remove}
                                        disabled={busy}
                                        data-testid="admin-community-tile-delete"
                                        className="mt-3 w-full border border-red-500/40 hover:bg-red-500/10 text-red-400 font-mono text-[10px] tracking-[0.14em] uppercase py-2 disabled:opacity-40"
                                >
                                        {busy ? "Deleting…" : failed ? "Failed · Retry" : "Delete"}
                                </button>
                        </div>
                </div>
        );
}

function CollabsPanel({ token, onSessionExpired }) {
        const [items, setItems] = useState([]);
        const [loading, setLoading] = useState(true);
        const [url, setUrl] = useState("");
        const [busy, setBusy] = useState(false);
        const [err, setErr] = useState("");

        const refresh = useCallback(async () => {
                setLoading(true);
                setErr("");
                try {
                        const r = await fetch(`${BACKEND}/api/admin/instagram/collabs`, {
                                headers: { "Authorization": `Bearer ${token}` },
                        });
                        if (r.status === 401) { onSessionExpired(); return; }
                        if (!r.ok) { setErr(`Failed to load (${r.status})`); return; }
                        const j = await r.json();
                        setItems(j.collabs || []);
                } catch (_e) {
                        setErr("Network error.");
                } finally {
                        setLoading(false);
                }
        }, [token, onSessionExpired]);

        useEffect(() => { refresh(); }, [refresh]);

        const add = async (e) => {
                e.preventDefault();
                setErr("");
                setBusy(true);
                try {
                        const r = await fetch(`${BACKEND}/api/admin/instagram/collabs`, {
                                method: "POST",
                                headers: { "Content-Type": "application/json",
                                           "Authorization": `Bearer ${token}` },
                                body: JSON.stringify({ permalink: url }),
                        });
                        if (r.status === 401) { onSessionExpired(); return; }
                        if (!r.ok) {
                                let msg = `HTTP ${r.status}`;
                                try {
                                        const body = await r.json();
                                        const detail = body.detail;
                                        msg = typeof detail === "string" ? detail : JSON.stringify(detail);
                                } catch { /* ignore */ }
                                setErr(msg);
                                return;
                        }
                        setUrl("");
                        await refresh();
                } catch (_e) {
                        setErr("Network error.");
                } finally {
                        setBusy(false);
                }
        };

        const remove = async (id) => {
                if (!window.confirm("Remove this collab from the storefront strip?")) return;
                try {
                        const r = await fetch(`${BACKEND}/api/admin/instagram/collabs/${id}`, {
                                method: "DELETE",
                                headers: { "Authorization": `Bearer ${token}` },
                        });
                        if (r.status === 401) { onSessionExpired(); return; }
                        if (r.ok) setItems((prev) => prev.filter((x) => x.id !== id));
                } catch { /* ignore */ }
        };

        return (
                <div data-testid="admin-collabs-panel" className="mt-14 border-t border-white/10 pt-10">
                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#FF9F1C]">
                                Instagram · Collabs
                        </p>
                        <h2 className="mt-2 font-display text-2xl uppercase text-white">
                                Paste a post URL to feature it
                        </h2>
                        <p className="mt-2 font-sans text-sm text-white/60">
                                Any public Instagram post — reel, image, or carousel. We grab the thumbnail
                                and store it, so IG CDN expiry never breaks the tile.
                        </p>

                        <form onSubmit={add} className="mt-6 flex flex-col sm:flex-row gap-3">
                                <input
                                        type="url"
                                        value={url}
                                        onChange={(e) => setUrl(e.target.value)}
                                        placeholder="https://www.instagram.com/reel/DQo-9hIku6r/"
                                        data-testid="admin-collab-url-input"
                                        className="flex-1 bg-black border border-white/20 focus:border-[#FF9F1C] outline-none text-white px-3 py-2 font-mono text-sm"
                                />
                                <button
                                        type="submit"
                                        disabled={busy || !url}
                                        data-testid="admin-collab-add-submit"
                                        className="bg-[#FF9F1C] text-black font-mono text-[12px] tracking-[0.18em] uppercase px-6 py-2 disabled:opacity-40"
                                >
                                        {busy ? "Adding…" : "Add Collab"}
                                </button>
                        </form>

                        {err && (
                                <p data-testid="admin-collab-error" className="mt-3 font-mono text-[11px] tracking-[0.14em] uppercase text-red-400">
                                        {err}
                                </p>
                        )}

                        <div className="mt-8">
                                <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">
                                        {loading ? "Loading…" : `${items.length} collab${items.length === 1 ? "" : "s"} live`}
                                </p>
                        </div>

                        {!loading && items.length === 0 && (
                                <div className="mt-4 border border-dashed border-white/15 py-10 text-center">
                                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/40">
                                                No collabs yet. Paste a URL above to publish one.
                                        </p>
                                </div>
                        )}

                        {items.length > 0 && (
                                <div
                                        className="mt-4 grid gap-4"
                                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
                                >
                                        {items.map((c) => (
                                                <div key={c.id} data-testid="admin-collab-tile" className="border border-white/10 bg-[#0F0F0F] overflow-hidden">
                                                        <a href={c.permalink} target="_blank" rel="noopener noreferrer">
                                                                <div style={{ aspectRatio: "1/1" }} className="bg-black">
                                                                        <img
                                                                                src={`${BACKEND}${c.thumbnail_url}`}
                                                                                alt={c.caption || "collab post"}
                                                                                loading="lazy"
                                                                                className="w-full h-full object-cover"
                                                                        />
                                                                </div>
                                                        </a>
                                                        <div className="p-3">
                                                                <p className="font-mono text-[10px] tracking-[0.14em] uppercase text-[#FF9F1C]">
                                                                        @{c.creator_username || "unknown"}
                                                                </p>
                                                                <p className="mt-1 font-sans text-[11px] text-white/60 line-clamp-2" style={{
                                                                        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                                                                }}>
                                                                        {c.caption || " "}
                                                                </p>
                                                                <button
                                                                        type="button"
                                                                        onClick={() => remove(c.id)}
                                                                        data-testid="admin-collab-delete"
                                                                        className="mt-3 w-full border border-red-500/40 hover:bg-red-500/10 text-red-400 font-mono text-[10px] tracking-[0.14em] uppercase py-2"
                                                                >
                                                                        Remove
                                                                </button>
                                                        </div>
                                                </div>
                                        ))}
                                </div>
                        )}
                </div>
        );
}

function Console({ token, onLogout }) {
        const [images, setImages] = useState([]);
        const [loading, setLoading] = useState(true);
        const [err, setErr] = useState("");

        const refresh = useCallback(async () => {
                setLoading(true);
                setErr("");
                try {
                        const r = await fetch(`${BACKEND}/api/admin/community/list`, {
                                headers: { "Authorization": `Bearer ${token}` },
                        });
                        if (r.status === 401) { onLogout(); return; }
                        if (!r.ok) { setErr(`Failed to load (${r.status})`); return; }
                        const j = await r.json();
                        setImages(j.images || []);
                } catch (_e) {
                        setErr("Network error.");
                } finally {
                        setLoading(false);
                }
        }, [token, onLogout]);

        useEffect(() => { refresh(); }, [refresh]);

        return (
                <div data-testid="admin-community-console" className="w-full max-w-[1200px] mx-auto">
                        <div className="flex items-center justify-between">
                                <div>
                                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#FF9F1C]">
                                                Loadout · Ops Console
                                        </p>
                                        <h1 className="mt-1 font-display text-3xl uppercase leading-tight text-white">
                                                Community Gallery
                                        </h1>
                                </div>
                                <button
                                        type="button"
                                        onClick={onLogout}
                                        data-testid="admin-community-logout"
                                        className="font-mono text-[11px] tracking-[0.16em] uppercase border border-white/20 hover:border-white/50 text-white/70 px-4 py-2"
                                >
                                        Sign out
                                </button>
                        </div>

                        <div className="mt-8">
                                <UploadZone
                                        token={token}
                                        onUploaded={(res) => {
                                                if (res.sessionExpired) { onLogout(); return; }
                                                refresh();
                                        }}
                                />
                        </div>

                        <div className="mt-10 flex items-center justify-between">
                                <p
                                        data-testid="admin-community-count"
                                        className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60"
                                >
                                        {loading ? "Loading…" : `${images.length} images in gallery`}
                                </p>
                                <button
                                        type="button"
                                        onClick={refresh}
                                        data-testid="admin-community-refresh"
                                        className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/50 hover:text-[#FF9F1C]"
                                >
                                        Refresh
                                </button>
                        </div>

                        {err && (
                                <p
                                        data-testid="admin-community-error"
                                        className="mt-4 font-mono text-[11px] tracking-[0.14em] uppercase text-red-400"
                                >
                                        {err}
                                </p>
                        )}

                        {!loading && images.length === 0 && (
                                <div
                                        data-testid="admin-community-empty"
                                        className="mt-6 border border-dashed border-white/15 py-12 text-center"
                                >
                                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/40">
                                                Gallery is empty. Upload the first setup to publish it live.
                                        </p>
                                </div>
                        )}

                        {images.length > 0 && (
                                <div
                                        data-testid="admin-community-grid"
                                        className="mt-6 grid gap-4"
                                        style={{ gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))" }}
                                >
                                        {images.map((img) => (
                                                <ImageTile
                                                        key={img.id}
                                                        image={img}
                                                        token={token}
                                                        onDeleted={(res) => {
                                                                if (res.sessionExpired) { onLogout(); return; }
                                                                setImages((prev) => prev.filter((x) => x.id !== res.id));
                                                        }}
                                                />
                                        ))}
                                </div>
                        )}

                        <CollabsPanel token={token} onSessionExpired={onLogout} />
                </div>
        );
}

export default function AdminCommunity() {
        const [token, saveToken, clearToken] = useToken();

        return (
                <div
                        data-testid="admin-community-page"
                        className="min-h-screen bg-[#0A0A0A] text-white flex items-start justify-center py-20 px-6"
                >
                        {!token ? (
                                <LoginPanel onLogin={saveToken} />
                        ) : (
                                <Console token={token} onLogout={clearToken} />
                        )}
                </div>
        );
}
